import useJDialog from '@/hooks/useJDialog';
import useJSnackbar from '@/hooks/useJSnackbar';

import { JButton } from '@atoms/JButton';
import JTableCell from '@atoms/JTabelCell';
import { ShowMoreButton } from '@atoms/ShowMoreButton';
import { StatusChip } from '@atoms/StatusChip';
import { IconSize } from '@constants';

import { Bars2Icon } from '@heroicons/react/24/outline';
import { Menu, Stack, TableRowProps, useTheme } from '@mui/material';
import GetVideoStepItemDto from '@settings/models/dto/get-video-step-item.dto';
import {
  useDisableVideoStepMutation,
  useEnableVideoStepMutation,
} from '@settings/mutations/videoStepsMutations';
import { DialogContentStyled } from '@styled/dialog-content-styled';
import { MenuItemStyled } from '@styled/menu-item.styled';
import { TableRowStyled } from '@styled/table-row.styled';
import { UseMutateFunction } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import CvVideoStepForm from '../CvVideoStepForm';

interface Props extends TableRowProps {
  row: GetVideoStepItemDto;
  providedDrag: DraggableProvided;
}

type disbaleMutationType = UseMutateFunction<
  AxiosResponse<string, any>,
  unknown,
  { id: string },
  unknown
>;

type enableMutationType = UseMutateFunction<
  AxiosResponse<string, any>,
  unknown,
  { id: string },
  unknown
>;

export const CvVideoStepRow = ({ row, providedDrag }: Props) => {
  const {
    duration,
    id,
    name,
    negativeExplanation,
    positiveExplanation,
    rank,
    required,
    deletedAt,
  } = row;
  const theme = useTheme();
  const { t } = useTranslation();
  const { closeJDialog } = useJDialog();
  const { showSnackbar } = useJSnackbar();
  const {
    mutate: enableVideoStepMutate,
    isSuccess: enableSuccess,
    isError: enableError,
  } = useEnableVideoStepMutation();
  const {
    mutate: disableVideoStepMutate,
    isSuccess: disableSuccess,
    isError: disableError,
  } = useDisableVideoStepMutation();
  const { showJDialog } = useJDialog();

  const [rowMenuanchorEl, setRowMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setRowMenuAnchorEl(event.currentTarget);
  };
  const handleRowMenuClose = () => {
    setRowMenuAnchorEl(null);
  };

  const openDisableDialog = (
    videoStepName: string,
    videoStepId: string,
    disableMutation: disbaleMutationType,
  ): void => {
    handleRowMenuClose();
    showJDialog(
      t('DISABLE_VIDEO_STEP'),
      <DisableDialogContent
        id={videoStepId}
        name={videoStepName}
        disableMutation={disableMutation}
      />,
    );
  };

  const openEnableDialog = (
    videoStepName: string,
    videoStepId: string,
    enableMutation: enableMutationType,
  ): void => {
    handleRowMenuClose();
    showJDialog(
      t('ENABLE_VIDEO_STEP'),
      <EnableDialogContent
        id={videoStepId}
        name={videoStepName}
        enableMutation={enableMutation}
      />,
    );
  };

  const openEditDialog = () => {
    handleRowMenuClose();
    showJDialog(
      t('EDIT_STEP'),
      <CvVideoStepForm defaultRank={rank} values={row} />,
      'filter',
    );
  };
  const open = Boolean(rowMenuanchorEl);

  const handleChangeStatus = (isStatusActive: boolean) => {
    isStatusActive
      ? openDisableDialog(`${name}`, id, disableVideoStepMutate)
      : openEnableDialog(`${name}`, id, enableVideoStepMutate);
  };

  useEffect(() => {
    if (!enableSuccess) return;

    closeJDialog();
  }, [enableSuccess]);
  useEffect(() => {
    if (!disableSuccess) return;

    closeJDialog();
  }, [disableSuccess]);

  useEffect(() => {
    if (enableError) showSnackbar(t('API_ERROR'), 'error');
  }, [enableError]);
  useEffect(() => {
    if (disableError) showSnackbar(t('API_ERROR'), 'error');
  }, [disableError]);

  return (
    <TableRowStyled
      key={id}
      data-test={`video-step-row-${name}`}
      ref={providedDrag.innerRef}
      {...providedDrag.draggableProps}
      {...providedDrag.dragHandleProps}
    >
      <JTableCell>
        <Bars2Icon
          height={IconSize.buttonIcon}
          color={theme.palette.primary.main}
        />
      </JTableCell>
      <JTableCell isbold>{name}</JTableCell>
      <JTableCell>{rank}</JTableCell>
      <JTableCell>{duration}</JTableCell>
      <JTableCell>{required ? t('YES') : t('NO')}</JTableCell>
      <JTableCell>{positiveExplanation}</JTableCell>
      <JTableCell>{negativeExplanation}</JTableCell>
      <JTableCell>
        <StatusChip
          variant={deletedAt ? 'danger' : 'success'}
          label={deletedAt ? t('INACTIVE') : t('ACTIVE')}
        />
      </JTableCell>
      <JTableCell>
        <ShowMoreButton onClick={handleClick} open={open} />
        <Menu
          id="basic-menu"
          anchorEl={rowMenuanchorEl}
          open={open}
          onClose={handleRowMenuClose}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItemStyled
            onClick={() => handleChangeStatus(!deletedAt)}
            data-qa="close"
            red={!deletedAt}
            green={!!deletedAt}
          >
            {!deletedAt ? t('DESACTIVATE') : t('ACTIVATE')}
          </MenuItemStyled>
          {!deletedAt && (
            <MenuItemStyled
              onClick={() => openEditDialog()}
              data-qa="edit"
              red={false}
              green={false}
            >
              {t('EDIT_STEP')}
            </MenuItemStyled>
          )}
        </Menu>
      </JTableCell>
    </TableRowStyled>
  );
};

const DisableDialogContent = ({
  id,
  disableMutation,
}: {
  name: string;
  id: string;
  disableMutation: disbaleMutationType;
}) => {
  const { closeJDialog } = useJDialog();
  const { t } = useTranslation();
  return (
    <Stack gap={2}>
      <DialogContentStyled>{t('DISABLE_VIDEO_STEP_TEXT')}</DialogContentStyled>

      <Stack direction={'row'} gap={1}>
        <JButton
          onClick={() => disableMutation({ id })}
          variant="contained"
          color="error"
        >
          {t('DEACTIVATE')}
        </JButton>
        <JButton onClick={closeJDialog} variant="contained" color="info">
          Annuler
        </JButton>
      </Stack>
    </Stack>
  );
};

const EnableDialogContent = ({
  name,
  id,
  enableMutation,
}: {
  name: string;
  id: string;
  enableMutation: enableMutationType;
}) => {
  const { closeJDialog } = useJDialog();
  const { t } = useTranslation();
  return (
    <Stack gap={2}>
      <DialogContentStyled>{t('ENABLE_VIDEO_STEP_TEXT')}</DialogContentStyled>

      <Stack direction={'row'} gap={1}>
        <JButton
          data-qa="enable"
          onClick={() => enableMutation({ id })}
          variant="contained"
          color="secondary"
        >
          {t('ACTIVATE')}
        </JButton>
        <JButton
          data-qa="close"
          onClick={closeJDialog}
          variant="contained"
          color="info"
        >
          {t('CANCEL')}
        </JButton>
      </Stack>
    </Stack>
  );
};
